import React, { useMemo, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';

// ECDEX-3840 (slice 3): whether the candidate is viewing the international
// portal, remembered across the session. Replaces the `international` Redux
// slice. App works it out (from the URL, region and the report's country) and
// sets it here; the route containers OR it with their own isInternational()
// check. Like the slice it replaces, once true it stays true until the
// provider unmounts. Components read one piece through a selector, so one
// that only sets the value does not re-render when it changes.
interface InternationalContextValue {
  international: boolean;
  setInternational: (value: boolean) => void;
}

const InternationalContext = createContext<InternationalContextValue>({
  international: false,
  setInternational: () => {},
});

export const InternationalProvider: React.FC<{
  children?: React.ReactNode;
  initialValue?: boolean;
}> = ({ children, initialValue = false }) => {
  const [international, setInternational] = useState(initialValue);
  const value = useMemo(
    () => ({ international, setInternational }),
    [international],
  );
  return (
    <InternationalContext.Provider value={value}>
      {children}
    </InternationalContext.Provider>
  );
};

export const useInternational = () =>
  useContextSelector(InternationalContext, value => value.international);

export const useSetInternational = () =>
  useContextSelector(InternationalContext, value => value.setInternational);
